import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { TabProps } from './types'
import { ChartWrapper } from './ChartWrapper'
import api from '../../lib/api'
import { useDrilldownAction } from '../drilldown'
import { useMenuSettings } from '../../contexts/MenuSettingsContext'
import { useView } from '../../contexts/ViewContext'
import { ExternalLink, Info } from 'lucide-react'

// Color palettes for pie charts
// CR-4167: 7 approval stages matching onboarding workflow
const APPROVAL_COLORS = ['#3b82f6', '#f59e0b', '#6b7280', '#818cf8', '#10b981', '#ef4444', '#8b5cf6']
const RISK_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#7c3aed']
const QUESTIONNAIRE_COLORS = ['#10b981', '#f59e0b', '#ef4444']
const CONTRACT_COLORS = ['#10b981', '#3b82f6']

// Custom legend component for pie charts
interface LegendItem {
  name?: string
  status?: string
  percentage?: number
  value?: number
}

function PieChartLegend({ data, colors }: { data: LegendItem[]; colors: string[] }) {
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={item.name || item.status} className="flex items-center">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: colors[index % colors.length] }}
          />
          <span className="text-sm">
            {item.name || item.status} - {item.percentage?.toFixed(0) || item.value}%
          </span>
        </div>
      ))}
    </div>
  )
}

interface ComplianceDataState {
  live_contracts: number
  docs_awaiting_read: number
  suppliers_by_approval: Array<{ name: string; value: number; percentage: number }>
  suppliers_by_risk: Array<{ name: string; value: number; percentage: number }>
  questionnaires_by_status: Array<{ name: string; value: number; percentage: number }>
  contracts_by_auto_renew: Array<{ name: string; value: number; percentage: number }>
  credit_score_distribution: Array<{ range: string; count: number }>
  approvals_expiring: Array<{ period: string; count: number }>
  expiring_contracts: Array<{ period: string; count: number }>
  br_meetings: Array<{ month: string; completed: number; scheduled: number }>
}

export function ComplianceTab({ data: _data, filters, dataPeriod: _dataPeriod, orderType: _orderType }: TabProps) {
  const navigate = useNavigate()
  const [complianceData, setComplianceData] = useState<ComplianceDataState | null>(null)
  const [loading, setLoading] = useState(true)

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()
  // CR-4814: pie counts must use the same view context as drill-down fetches,
  // otherwise the "Suppliers by Approval Stages" segments diverge from what
  // the drill-down returns (graph showed a count, drill-down returned empty).
  const { viewLevel, getViewQueryParams } = useView()

  // Compliance-only tenants (e.g. MAB running the onboarding module without
  // purchasing or credit tracking) don't have Contracts / GRNs / Credit Score
  // data and never will. The four contract/credit widgets always render
  // empty for them, which reads as "the feature is broken" rather than
  // "this feature isn't enabled for your tenant". Hide them in that mode.
  const { isComplianceMode } = useMenuSettings()
  const showContractWidgets = !isComplianceMode
  const showCreditWidget = !isComplianceMode

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
    // CR-4800: compliance-only filters surfaced by DashboardFilters
    ...(filters?.approvalStage && { approval_stage: filters.approvalStage }),
    ...(filters?.riskCategory && { risk_category: filters.riskCategory }),
  }


  // Fetch compliance data from multiple APIs
  useEffect(() => {
    // Backend pagination caps page_size at 100 on most ViewSets. Asking for
    // page_size=1000 silently truncates to the first 100 rows, which on MAB
    // meant the Approvals Expiring chart rendered 0 despite 346 approved
    // fillings with renewal dates — the ones in the 30/60/90 buckets sat on
    // later pages that were never fetched. This helper walks the `next` link
    // until exhausted.
    // Walk paginated DRF responses via ?page=N (axios' baseURL makes the
    // absolute `next` URL awkward — concatenating it would double the /api
    // prefix). Capped at 50 pages to avoid infinite loops if a page lies.
    const fetchAll = async <T = Record<string, unknown>>(
      path: string,
      params: Record<string, unknown> = {},
    ): Promise<T[]> => {
      const rows: T[] = []
      for (let page = 1; page <= 50; page++) {
        try {
          const resp = await api.get<{ results?: T[]; next?: string | null } | T[]>(path, {
            params: { ...params, page_size: 100, page },
          })
          if (Array.isArray(resp.data)) {
            rows.push(...(resp.data as T[]))
            return rows
          }
          rows.push(...(resp.data.results || []))
          if (!resp.data.next) return rows
        } catch {
          return rows
        }
      }
      return rows
    }

    const fetchComplianceData = async () => {
      try {
        setLoading(true)

        // CR-4789 structural fix: the Approval Stages pie now reads the
        // bucket counts from the same backend endpoint the drill-down
        // filter uses, so the pie total and each drill-down segment are
        // guaranteed to match. Previously this component re-did the
        // bucketing client-side from /suppliers/profiles/ +
        // /suppliers/onboarding/ + /purchasing/contract-prices/ and
        // disagreed with the drill-down whenever workflow/profile data
        // drifted.
        const [
          approvalBucketsResp,
          riskAssessments,
          certifications,
          questionnaires,
          contracts,
          creditScores,
        ] = await Promise.all([
          api.get<{ buckets: Record<string, number>; total: number }>(
            '/drilldown/suppliers_by_approval/buckets/',
            { params: { view_level: viewLevel, ...getViewQueryParams() } },
          ).catch(() => ({ data: { buckets: {}, total: 0 } })),
          // CR-4790: dropped /suppliers/onboarding/ — nothing on this tab
          // reads SupplierOnboarding rows any more. The KPI that used to
          // derive from it now counts distinct suppliers from the
          // questionnaires response (same source as Pending Due Diligence).
          // /suppliers/profiles/ was already dropped in CR-4789 when the
          // approval pie moved to the backend bucket endpoint.
          fetchAll<Record<string, unknown>>('/suppliers/risk-assessments/'),
          fetchAll<Record<string, unknown>>('/suppliers/certifications/'),
          fetchAll<Record<string, unknown>>('/onboarding/filled-datasets/'),
          fetchAll<Record<string, unknown>>('/purchasing/contract-prices/'),
          fetchAll<Record<string, unknown>>('/suppliers/credit-scores/'),
        ])

        // Calculate active contracts first (still used for other widgets
        // like the auto-renew pie and live-contracts counter).
        const activeContracts = contracts.filter((c: Record<string, unknown>) =>
          c['status'] === 'active'
        )
        const liveContracts = activeContracts.length

        // CR-4789: bucket counts come from the backend now — no more
        // client-side workflow/contract joining for approval stages.
        const approvalCounts: Record<string, number> = {
          'Not Invited': 0,
          'Invited': 0,
          'In Progress': 0,
          'Submitted / Under Review': 0,
          'More Info Required': 0,
          'Approved': 0,
          'Declined': 0,
          'Contract Awarded': 0,
          ...(approvalBucketsResp.data?.buckets || {}),
        }

        const totalApproval = Object.values(approvalCounts).reduce((a, b) => a + b, 0) || 1

        // Calculate suppliers by risk category from risk assessments
        const riskCounts: Record<string, number> = {
          Low: 0,
          Medium: 0,
          High: 0,
          Critical: 0,
        }

        riskAssessments.forEach((assessment: Record<string, unknown>) => {
          const level = (assessment['overall_risk_level'] as string) || 'medium'
          if (level === 'low') riskCounts['Low']++
          else if (level === 'medium') riskCounts['Medium']++
          else if (level === 'high') riskCounts['High']++
          else if (level === 'critical') riskCounts['Critical']++
        })

        const totalRisk = Object.values(riskCounts).reduce((a, b) => a + b, 0) || 1

        // Bucket FilledDataset by its real status (onboarding/models/choices.py
        // :FilledDatasetStatus). Previously this checked status==='completed'
        // which is not a value the backend ever sets — so "Complete" always
        // rendered 0 even when 346 approved questionnaires existed.
        const questionnaireCounts: Record<string, number> = {
          Complete: 0,
          'In Progress': 0,
          'Not Started': 0,
        }

        questionnaires.forEach((q: Record<string, unknown>) => {
          const st = (q['status'] as string) || ''
          if (st === 'approved' || st === 'submitted' || st === 'under_review') {
            questionnaireCounts['Complete']++
          } else if (st === 'in_progress' || st === 'more_info_required' || st === 'rejected') {
            questionnaireCounts['In Progress']++
          } else {
            questionnaireCounts['Not Started']++
          }
        })

        const totalQuestionnaires = Object.values(questionnaireCounts).reduce((a, b) => a + b, 0) || 1

        // "Approvals Expiring" buckets approved FilledDataset records by
        // expires_at (when the supplier's approval lapses and needs renewal).
        // Previously this queried SupplierCertification.expiry_date which is a
        // different concept (e.g. ISO cert expiry) and always rendered 0 even
        // when 346 approved questionnaires had renewal dates in the next year.
        // Certifications are still fetched because they feed other widgets.
        void certifications  // keep variable used without changing the fetch
        const now = new Date()
        const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
        const in60Days = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)
        const in90Days = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)

        let expiring30 = 0, expiring60 = 0, expiring90 = 0

        questionnaires.forEach((q: Record<string, unknown>) => {
          if (q['status'] !== 'approved' || !q['expires_at']) return
          const exp = new Date(q['expires_at'] as string)
          if (exp <= in30Days && exp > now) expiring30++
          else if (exp <= in60Days && exp > in30Days) expiring60++
          else if (exp <= in90Days && exp > in60Days) expiring90++
        })

        // CR-4790: "Suppliers Awaiting Review" must mirror the Pending Due
        // Diligence tab on Supplier Onboarding — distinct suppliers with a
        // questionnaire in submitted / under_review / more_info_required /
        // rejected. Previously this read SupplierOnboarding rows filtered
        // to {submitted, more_info_required}, which is a different data set
        // and a different status list, so the dashboard KPI showed 0 while
        // the tab badge had 3 suppliers awaiting review.
        const PENDING_REVIEW_STATUSES = new Set([
          'submitted', 'under_review', 'more_info_required', 'rejected',
        ])
        const suppliersAwaitingReview = new Set<number>()
        questionnaires.forEach((q: Record<string, unknown>) => {
          const st = (q['status'] as string) || ''
          const supplierId = q['supplier']
          if (PENDING_REVIEW_STATUSES.has(st) && typeof supplierId === 'number') {
            suppliersAwaitingReview.add(supplierId)
          }
        })
        const docsAwaitingRead = suppliersAwaitingReview.size

        // Calculate contracts by auto-renew from real contract data
        const autoRenewCount = activeContracts.filter((c: Record<string, unknown>) => c['auto_renew'] === true).length
        const manualRenewCount = liveContracts - autoRenewCount

        // Credit score distribution from actual credit score records
        const creditScoreDist: Record<string, number> = {
          'Excellent (800+)': 0,
          'Good (700-799)': 0,
          'Fair (600-699)': 0,
          'Poor (<600)': 0,
        }

        creditScores.forEach((cs: Record<string, unknown>) => {
          const score = cs['score'] as number
          if (score >= 800) creditScoreDist['Excellent (800+)']++
          else if (score >= 700) creditScoreDist['Good (700-799)']++
          else if (score >= 600) creditScoreDist['Fair (600-699)']++
          else creditScoreDist['Poor (<600)']++
        })

        // Calculate expiring contracts from real contract data
        const contractsExpiring30 = activeContracts.filter((c: Record<string, unknown>) => {
          if (!c['valid_to']) return false
          const validTo = new Date(c['valid_to'] as string)
          return validTo <= in30Days && validTo > now
        }).length
        const contractsExpiring60 = activeContracts.filter((c: Record<string, unknown>) => {
          if (!c['valid_to']) return false
          const validTo = new Date(c['valid_to'] as string)
          return validTo <= in60Days && validTo > in30Days
        }).length
        const contractsExpiring90 = activeContracts.filter((c: Record<string, unknown>) => {
          if (!c['valid_to']) return false
          const validTo = new Date(c['valid_to'] as string)
          return validTo <= in90Days && validTo > in60Days
        }).length

        setComplianceData({
          live_contracts: liveContracts,
          docs_awaiting_read: docsAwaitingRead,
          suppliers_by_approval: Object.entries(approvalCounts).map(([name, count]) => ({
            name,
            value: count,
            percentage: (count / totalApproval) * 100,
          })),
          suppliers_by_risk: Object.entries(riskCounts)
            .filter(([_, count]) => count > 0)
            .map(([name, count]) => ({
              name,
              value: count,
              percentage: (count / totalRisk) * 100,
            })),
          questionnaires_by_status: Object.entries(questionnaireCounts).map(([name, count]) => ({
            name,
            value: count,
            percentage: (count / totalQuestionnaires) * 100,
          })),
          contracts_by_auto_renew: [
            { name: 'Auto Renew', value: autoRenewCount, percentage: (autoRenewCount / (liveContracts || 1)) * 100 },
            { name: 'Manual Renew', value: manualRenewCount, percentage: (manualRenewCount / (liveContracts || 1)) * 100 },
          ],
          credit_score_distribution: Object.entries(creditScoreDist).map(([range, count]) => ({
            range,
            count,
          })),
          approvals_expiring: [
            { period: '30 days', count: expiring30 },
            { period: '60 days', count: expiring60 },
            { period: '90 days', count: expiring90 },
          ],
          expiring_contracts: [
            { period: '30 days', count: contractsExpiring30 },
            { period: '60 days', count: contractsExpiring60 },
            { period: '90 days', count: contractsExpiring90 },
          ],
          br_meetings: [],
        })
      } catch (error) {
        console.error('Error fetching compliance data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchComplianceData()
  }, [])

  const emptyData: ComplianceDataState = {
    live_contracts: 0,
    docs_awaiting_read: 0,
    suppliers_by_approval: [],
    suppliers_by_risk: [],
    questionnaires_by_status: [],
    contracts_by_auto_renew: [],
    credit_score_distribution: [],
    approvals_expiring: [],
    expiring_contracts: [],
    br_meetings: [],
  }

  const displayData = complianceData || emptyData

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading compliance data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* KPI Cards Row */}
      <div className={`grid ${showContractWidgets ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
        {/* Live Contracts — hidden for compliance-only tenants. */}
        {showContractWidgets && (
          <div className="bg-card rounded-lg shadow-sm p-6">
            <div className="text-center">
              <div className="text-lg font-semibold text-muted-foreground mb-4">Live Contracts</div>
              <div className="text-5xl font-bold text-blue-600">{displayData.live_contracts}</div>
              <p className="text-sm text-muted-foreground mt-2">Active supplier contracts</p>
            </div>
          </div>
        )}

        {/* Onboarding workflows awaiting customer review — the underlying
            query is onboardings filtered to status in {submitted, more_info_required}.
            Previously labelled "Documents Awaiting Read Receipt" which misled
            users into thinking it was document-acknowledgment counts. */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <div className="text-center">
            <div className="text-lg font-semibold text-muted-foreground mb-4">Suppliers Awaiting Review</div>
            <div className="text-5xl font-bold text-amber-600">{displayData.docs_awaiting_read}</div>
            <p className="text-sm text-muted-foreground mt-2">Suppliers in Pending Due Diligence</p>
          </div>
        </div>
      </div>

      {/* Pie Charts Row 1 - Suppliers by Approval and Risk */}
      <div className="grid grid-cols-2 gap-6">
        <ChartWrapper
          title="Suppliers by Approval Stages"
          data={displayData.suppliers_by_approval}
          exportFilename="suppliers-by-approval"
        >
          <div className="grid grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart style={{ cursor: 'pointer' }}>
                <Pie
                  data={displayData.suppliers_by_approval}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="value"
                  nameKey="name"
                  // CR-4789: Pass the clicked segment's bucket name so the
                  // drill-down filters to just that approval stage instead
                  // of showing every supplier regardless of stage.
                  onClick={(segment: { name?: string }) => openDrilldown(
                    'suppliers_by_approval',
                    { ...drilldownFilters, ...(segment?.name ? { approval_stage: segment.name } : {}) },
                    segment?.name,
                  )}
                >
                  {displayData.suppliers_by_approval.map((_: unknown, index: number) => (
                    <Cell key={`cell-${index}`} fill={APPROVAL_COLORS[index % APPROVAL_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number, name: string) => [`${value}`, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieChartLegend data={displayData.suppliers_by_approval} colors={APPROVAL_COLORS} />
          </div>
        </ChartWrapper>

        <ChartWrapper
          title="Suppliers by Risk Category"
          data={displayData.suppliers_by_risk}
          exportFilename="suppliers-by-risk"
        >
          <div className="grid grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart style={{ cursor: 'pointer' }}>
                <Pie
                  data={displayData.suppliers_by_risk}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="value"
                  nameKey="name"
                  // CR-4793: pass the clicked segment name so the drill-down
                  // filters to that risk level only. Without this the drill-down
                  // ignored the segment and returned every supplier.
                  onClick={(segment: { name?: string }) => openDrilldown(
                    'suppliers_by_risk',
                    { ...drilldownFilters, ...(segment?.name ? { risk_category: segment.name } : {}) },
                    segment?.name,
                  )}
                >
                  {displayData.suppliers_by_risk.map((_: unknown, index: number) => (
                    <Cell key={`cell-${index}`} fill={RISK_COLORS[index % RISK_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number, name: string) => [`${value}`, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieChartLegend data={displayData.suppliers_by_risk} colors={RISK_COLORS} />
          </div>
        </ChartWrapper>
      </div>

      {/* Pie Charts Row 2 - Questionnaires and Contracts */}
      <div className="grid grid-cols-2 gap-6">
        <ChartWrapper
          title="Questionnaires by Completion Status"
          data={displayData.questionnaires_by_status}
          exportFilename="questionnaires-by-status"
        >
          <div className="grid grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart style={{ cursor: 'pointer' }}>
                <Pie
                  data={displayData.questionnaires_by_status}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="value"
                  nameKey="name"
                  // CR-4791: route the click through the slice and open
                  // the drill-down on the matching tab. Previously onClick
                  // sat on the PieChart wrapper, so every slice landed on
                  // the default COMPLETE tab — and COMPLETE was itself
                  // broken (status__in string bug, fixed in the backend
                  // query builder under this same CR), so Complete slice
                  // → Complete tab returned 0 records despite 366 matches.
                  onClick={(segment: { name?: string }) => {
                    const name = segment?.name;
                    const tabKey =
                      name === 'Complete' ? 'complete'
                      : name === 'In Progress' ? 'in_progress'
                      : name === 'Not Started' ? 'not_started'
                      : undefined;
                    openDrilldown('questionnaires', drilldownFilters, undefined, tabKey);
                  }}
                >
                  {displayData.questionnaires_by_status.map((_: unknown, index: number) => (
                    <Cell key={`cell-${index}`} fill={QUESTIONNAIRE_COLORS[index % QUESTIONNAIRE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number, name: string) => [`${value}`, name]} />
              </PieChart>
            </ResponsiveContainer>
            <div>
              <PieChartLegend data={displayData.questionnaires_by_status} colors={QUESTIONNAIRE_COLORS} />
              <button
                onClick={() => navigate('/questionnaire')}
                className="mt-3 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                Manage Questionnaires
              </button>
            </div>
          </div>
        </ChartWrapper>

        {showContractWidgets && (
        <ChartWrapper
          title="Contracts by Auto Renew: Yes/No"
          data={displayData.contracts_by_auto_renew}
          exportFilename="contracts-by-auto-renew"
        >
          <div className="grid grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart style={{ cursor: 'pointer' }}>
                <Pie
                  data={displayData.contracts_by_auto_renew}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="value"
                  nameKey="name"
                  // CR-4792: route the click through the slice so we can scope
                  // the drill-down to just that bucket (Auto Renew / Manual
                  // Renew). Previously onClick sat on the PieChart wrapper so
                  // the drill-down fired with no bucket and returned all
                  // active contracts regardless of which slice was clicked.
                  onClick={(segment: { name?: string }) => openDrilldown(
                    'contracts_by_auto_renew',
                    { ...drilldownFilters, ...(segment?.name ? { auto_renew: segment.name } : {}) },
                  )}
                >
                  {displayData.contracts_by_auto_renew.map((_: unknown, index: number) => (
                    <Cell key={`cell-${index}`} fill={CONTRACT_COLORS[index % CONTRACT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number, name: string) => [`${value}`, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieChartLegend data={displayData.contracts_by_auto_renew} colors={CONTRACT_COLORS} />
          </div>
        </ChartWrapper>
        )}
      </div>

      {/* Bar Charts Row - Credit Score and Approvals Expiring */}
      <div className={`grid ${showCreditWidget ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
        {showCreditWidget && (
        <ChartWrapper
          title="Credit Score Distribution"
          data={displayData.credit_score_distribution}
          exportFilename="credit-score-distribution"
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={displayData.credit_score_distribution}
              layout="vertical"
              onClick={() => openDrilldown('credit_scores', drilldownFilters)}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="range" tick={{ fontSize: 10 }} width={120} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="count" fill="#3b82f6" name="Suppliers" radius={[0, 4, 4, 0]}>
                {displayData.credit_score_distribution.map((_entry: unknown, index: number) => {
                  const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444']
                  return <Cell key={`cell-${index}`} fill={colors[index]} />
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
        )}

        <ChartWrapper
          title="Approvals Expiring"
          data={displayData.approvals_expiring}
          exportFilename="approvals-expiring"
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={displayData.approvals_expiring}
              onClick={() => openDrilldown('approvals_expiring', drilldownFilters)}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="count" fill="#f59e0b" name="Expiring" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Final Row - Expiring Contracts (hidden in compliance mode) and BR Meetings */}
      <div className={`grid ${showContractWidgets ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
        {showContractWidgets && (
        <ChartWrapper
          title="Expiring Contracts"
          data={displayData.expiring_contracts}
          exportFilename="expiring-contracts"
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={displayData.expiring_contracts}
              onClick={() => openDrilldown('expiring_contracts', drilldownFilters)}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="count" fill="#ef4444" name="Contracts" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
        )}

        <ChartWrapper
          title="Business Review Meetings"
          data={displayData.br_meetings}
          exportFilename="br-meetings"
        >
          <div className="flex flex-col items-center justify-center h-[200px] text-center px-6 gap-2">
            <p className="text-sm text-muted-foreground">Business review meetings live on the Calendar.</p>
            <button
              onClick={() => navigate('/calendar')}
              className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> Open Calendar
            </button>
          </div>
        </ChartWrapper>
      </div>

      {/* Info Banner */}
      <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 flex-shrink-0 mt-0.5 text-blue-600" />
          <p className="text-sm text-blue-800">
            {showContractWidgets
              ? 'Compliance data is aggregated from supplier onboarding, risk assessments, certifications, and contract records.'
              : 'Compliance data is aggregated from supplier onboarding, risk assessments, and certifications.'}
          </p>
        </div>
      </div>
    </div>
  )
}
